/**
 * Page HTML generator — produces the main index.html document
 */
import { esc } from '@/utils/escape';
import { generateFontLinksHTML } from './fonts';
import { generateTrackingScriptsHTML } from './tracking';
import { generateComponentHTML } from './dispatcher';

export function generateHTML(components: any, order: string[], settings: any): string {
  // Separate header/footer/cookie from main content for semantic HTML
  const outsideMain = new Set(['header', 'footer', 'cookie', 'stickyCta']);
  const enabledComponents = order
    .filter((id) => components[id]?.enabled)
    .map((id) => components[id]);

  const headerHTML = enabledComponents
    .filter((c: any) => c.type === 'header')
    .map((c: any) => generateComponentHTML(c, settings))
    .join('\n\n');

  const mainContent = enabledComponents
    .filter((c: any) => !outsideMain.has(c.type))
    .map((c: any) => generateComponentHTML(c, settings))
    .join('\n\n');

  const footerHTML = enabledComponents
    .filter((c: any) => c.type === 'footer' || c.type === 'cookie' || c.type === 'stickyCta')
    .map((c: any) => generateComponentHTML(c, settings))
    .join('\n\n');

  // Build Premium FX class list
  const fxClasses: string[] = [];
  const intensity = settings.fxIntensity || 'medium';
  if (settings.fxOrbs) fxClasses.push('fx-orbs', `fx-orbs-${intensity}`);
  if (settings.fxGrain)
    fxClasses.push('fx-grain', `fx-grain-${intensity}`);
  if (settings.fxGlass) fxClasses.push('fx-glass-cards');
  const fxClassStr = fxClasses.length ? ' ' + fxClasses.join(' ') : '';

  // Grain overlay element
  const grainOverlay = settings.fxGrain
    ? '\n  <!-- Grain Overlay -->\n  <div class="fx-grain-overlay" aria-hidden="true"></div>\n'
    : '';

  return `<!DOCTYPE html>
<html lang="de"${settings.darkMode ? ' class="dark"' : ''}>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="description" content="${esc(settings.siteDescription)}">
  <title>${esc(settings.siteName)}</title>

  ${generateFontLinksHTML(settings)}

  <!-- Favicon -->
  <link rel="icon" href="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='.9em' font-size='90'>${settings.faviconEmoji || '\u{1F3E2}'}</text></svg>">

  <!-- Styles -->
  <link rel="stylesheet" href="css/tokens.css">
  <link rel="stylesheet" href="css/components.css">
</head>
<body data-style="${esc(settings.stylePreset || 'signature')}" data-density="${esc(settings.designDensity || 'balanced')}"${fxClassStr ? ` class="${fxClassStr.trim()}"` : ''}>
  <!-- Skip Link -->
  <a href="#main" class="skip-link">Zum Inhalt springen</a>
${grainOverlay}
${headerHTML}

  <main id="main">
${mainContent}
  </main>

${footerHTML}

  <!-- Scripts -->
  ${generateTrackingScriptsHTML(settings)}
  <script src="js/main.js"><${'/'}script>
</body>
</html>`;
}

/**
 * Generate body content — renders all enabled components in order
 */
export function generateBodyContent(components: any, order: string[], settings: any): string {
  let html = '';

  order.forEach((id) => {
    const comp = components[id];
    if (!comp?.enabled) return;

    html += generateComponentHTML(comp, settings);
  });

  return html;
}
